import React from "react";
import * as moment from "moment";
import * as _ from "lodash";
import {
    useTable,
    usePagination,
    useSortBy,
    useFilters,
    useGroupBy,
    useExpanded,
    useRowSelect,
} from "react-table";
import { useDrop } from "react-dnd";
import { Formik, Field, Form, ErrorMessage } from "formik";

import { ModelApi } from "../api/modelApi";
import { FormField, NumberField, SelectField } from "../common/forms";
import { UiUtils } from "../common/uiUtils";

interface ComboModelFormProps {
    projectId: string;
    submit: () => void;
    cancel: () => void;
    modelSettingsIds: any[];
}

interface ModelListProps {
    models: any[];
    addModel: (model) => void;
    removeModel: (id) => void;
    checkLatestRevision: (model, useLatestRevision) => void;
}

const requiredMetadata = ["name", "validMonths"];

const createFormData = (values, projectId, models) => {
    return {
        projectId: projectId,
        name: values.name,
        validMonths: values.validMonths,
        modelSettingsId: values.modelSettingsId,
        subModels: models.map(m => ({ id: m.id, latestRevision: m.latestRevision })),
    }
}

const ComboModelForm: React.SFC<ComboModelFormProps> = props => {

    const [models, setModels] = React.useState([]);

    const modelSettingsIds = React.useMemo(() => {
        const none = [{ label: "None", value: "" }];
        return [
            ...none, ...Object.keys(props.modelSettingsIds).map(k => ({
                label: k,
                value: props.modelSettingsIds[k]
            }))
        ];
    }, []);

    return <div className="row border p-2">
               <Formik
                   enableReinitialize={true}
                   initialValues={{ name: "", validMonths: 12, modelSettingsId: "" }}
                   onSubmit={async (values, actions) => {
                       try {
                           const { data } = await ModelApi.createComboModel(createFormData(values, props.projectId, models));
                           UiUtils.notifySuccess("Combo model created.");
                           props.submit();
                       } catch (e) {
                       }
                   }}
                   validate={(values: any) => {
                       const errors: any = {};

                       for (let req of requiredMetadata) {
                           if (!values[req]) {
                               errors[req] = "Required";
                           }
                       }
                       return errors;
                   }}
                   render={({ values, errors, status, touched, isSubmitting, handleReset, setValues }) => (
                       <Form className="col" style={{ width: "auto" }}>
                           <div className="row">
                           <div className="col">
                           <div className="row">
                               <h6>Combined model information</h6>
                                   </div>
                                   <FormField name="name" label="Name *" />
                                   <NumberField name="validMonths" label="Validity (months) *" />
                               <SelectField name="modelSettingsId" label="Model settings ID" values={modelSettingsIds} />
                           </div>
                               <div className="col">
                                   <ModelList
                                       models={models}
                                       addModel={model => {
                                           if (_.find(models, m => m.id === model.id) == null) {
                                               setModels([...models, { ...model, latestRevision: true }]);
                                           }
                                       }}
                                       removeModel={id => setModels(_.filter(models, m => m.id !== id))}
                                       checkLatestRevision={(model, checked) => {
                                           const updatedModels = [...models];
                                           const updatedModel = _.find(updatedModels, m => m.id === model.id);
                                           updatedModel.latestRevision = checked;
                                           setModels(updatedModels);
                                       }}
                                   />
                                   </div>
                           </div>
                           <div className="row">
                           <button className="btn btn-primary mt-1" type="submit" disabled={isSubmitting || _.isEmpty(models)}>
                               Create
                           </button>

                           <button className="btn btn-secondary ml-1 mt-1" type="button" disabled={isSubmitting} onClick={props.cancel}>
                               Cancel
                           </button>
                               </div>
                       </Form>
                   )}/>
           </div>;
}

const ModelRow = props => {
    const { row } = props;
    return <tr {...row.getRowProps()}>
               {row.cells.map(cell => {
                   return (
                       <td
                           {...cell.getCellProps()}
                           style={{
                               padding: "10px",
                               border: "solid 1px gray",
                           }}>
                           {cell.render("Cell")}
                       </td>
                   );
               })}
           </tr>;
}

const ModelList: React.SFC<ModelListProps> = props => {

    const data = React.useMemo(() => props.models, [props.models]);
    const columns = React.useMemo(() => [
            {
                Header: "Name",
                accessor: "name"
            },
            {
                Header: "Revision",
                accessor: "revision"
            },
        {
            Header: "Use latest revision",
            accessor: "latestRevision",
            Cell: ({ value, row }) =>
                <input type="checkbox" checked={value} onChange={(event) => {
                    props.checkLatestRevision(row.original, event.target.checked);
                }}/>
        },
            {
                Header: "",
                accessor: "id",
                Cell: ({ value }) =>
                    <button className="btn btn-danger btn-sm" onClick={() => {
                        props.removeModel(value);
                    }}>
                        <span className="icon ion-md-close"></span>
                    </button>
            }
        ],
        [props.models]);

    const [{ isOver }, drop] = useDrop({
        accept: "modelRow",
        drop: (item: any) => {
            props.addModel(item.model);
        },
        collect: monitor => ({
            isOver: !!monitor.isOver(),
        }),
    });

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        state: {},
    } = useTable({ columns, data });

    return (
        <div className="col">
            <div className="row">
                <h6>Models</h6>
            </div>
            <div className="row">
                <table className="table" ref={drop} style={{ backgroundColor: isOver ? "green" : null }}>
                    <thead>
                    {headerGroups.map(headerGroup => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column, index) => (
                                <th
                                    {...column.getHeaderProps()}
                                    style={{
                                                borderBottom: "solid 3px",
                                                color: "black",
                                                fontWeight: "bold",
                                            }}>
                                    {column.render("Header")}
                                </th>
                            ))}
                        </tr>
                    ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                    {_.isEmpty(rows)
                        ? <tr>
                              <td colSpan={3} style={{ textAlign: "center" }}>Drag and drop models here</td>
                          </tr>
                        : rows.map(row => {
                            prepareRow(row);
                            return (
                                <ModelRow key={row.original.id} row={row}/>
                            );
                        })
                    }
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default ComboModelForm;